import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type RolePermissionValue,
  type SettingRole,
  type SettingRolePayload,
  useCreateSettingRoleMutation,
  useDeleteSettingRoleMutation,
  useGetSettingRolesQuery,
  useUpdateSettingRoleMutation,
} from '../../store/api/settingsApi'
import { usePagePermission } from '../../hooks/usePagePermission'
import SettingsRoleDialog from './SettingsRoleDialog'

type ColumnKind = 'text' | 'date' | 'permission'
type ColumnDef = { key: keyof SettingRole; label: string; kind?: ColumnKind }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'roleCodeUper', label: 'Role Code' },
  { key: 'roleName', label: 'Role Name' },
  { key: 'description', label: 'Description' },
  { key: 'purchase', label: 'Purchase', kind: 'permission' },
  { key: 'procurement', label: 'Procurement', kind: 'permission' },
  { key: 'saleContract', label: 'Sale Contract', kind: 'permission' },
  { key: 'saleControl', label: 'Sale Control', kind: 'permission' },
  { key: 'setting', label: 'Setting', kind: 'permission' },
  { key: 'virtualWarehouse', label: 'Virtual Warehouse', kind: 'permission' },
  { key: 'futures', label: 'Futures', kind: 'permission' },
  { key: 'position', label: 'Position', kind: 'permission' },
  { key: 'quality', label: 'Quality', kind: 'permission' },
  { key: 'masterData', label: 'Master Data', kind: 'permission' },
  { key: 'factory', label: 'Factory control', kind: 'permission' },
  { key: 'factoryBl', label: 'Bảo Lộc factory', kind: 'permission' },
  { key: 'factoryNc', label: 'Nguyên Cường factory', kind: 'permission' },
  { key: 'factoryNs', label: 'Nông Sản factory', kind: 'permission' },
  { key: 'factoryNt', label: 'Nhơn Trạch factory', kind: 'permission' },
  { key: 'factoryPaco', label: 'Pacorini factory', kind: 'permission' },
  { key: 'matchingPs', label: 'Matching P-S', kind: 'permission' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'date' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const actionToolbarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const filterBarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`.slice(-2)
  return `${day}-${month}-${year}`
}

const permissionText = (value: RolePermissionValue | null | undefined): string => {
  if (value === 0) return 'View'
  if (value === 1) return 'Edit'
  return 'No access'
}

const cellValue = (row: SettingRole, col: ColumnDef): string => {
  const value = row[col.key]
  if (value == null) return ''
  if (col.kind === 'date') return formatAccessDate(value)
  if (col.kind === 'permission') return permissionText(value as RolePermissionValue)
  return String(value)
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save role. Please try again.'
  if (!('data' in error)) return 'Failed to save role. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save role. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save role. Please try again.'
}

const emptyDeleteDialogState = {
  open: false,
  error: '',
}

export default function SettingsRoleListView() {
  const { canEdit } = usePagePermission()
  const [selectedRoleCodeUper, setSelectedRoleCodeUper] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [roleDialogMode, setRoleDialogMode] = useState<'new' | 'edit'>('new')
  const [deleteDialog, setDeleteDialog] = useState(emptyDeleteDialogState)

  const { data: rolesData, isLoading, isFetching, error } = useGetSettingRolesQuery()
  const [createRole, { isLoading: isCreating }] = useCreateSettingRoleMutation()
  const [updateRole, { isLoading: isUpdating }] = useUpdateSettingRoleMutation()
  const [deleteRole, { isLoading: isDeleting }] = useDeleteSettingRoleMutation()

  const baseRows = useMemo(() => rolesData?.items ?? [], [rolesData])
  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase()
    if (!needle) return baseRows
    return baseRows.filter((row) =>
      TABLE_COLUMNS.some((col) => {
        const value = cellValue(row, col)
        return value.toLowerCase().includes(needle)
      }),
    )
  }, [baseRows, searchTerm])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.roleCodeUper === selectedRoleCodeUper) ?? null,
    [baseRows, selectedRoleCodeUper],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const hasSelection = Boolean(selectedRow)
  const currentDialogRole = selectedRow

  useEffect(() => {
    if (selectedRoleCodeUper && !baseRows.some((row) => row.roleCodeUper === selectedRoleCodeUper)) {
      setSelectedRoleCodeUper(null)
    }
  }, [baseRows, selectedRoleCodeUper])

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const handleOpenRoleDialog = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) return
    setRoleDialogMode(mode)
    setRoleDialogOpen(true)
  }

  const handleOpenDeleteDialog = () => {
    if (!selectedRow) return
    setDeleteDialog({ open: true, error: '' })
  }

  const handleSaveRole = async (payload: SettingRolePayload) => {
    if (roleDialogMode === 'new') {
      const saved = await createRole(payload).unwrap()
      setSelectedRoleCodeUper(saved.roleCodeUper)
      return
    }

    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }

    const saved = await updateRole({ roleCodeUper: selectedRow.roleCodeUper, body: payload }).unwrap()
    setSelectedRoleCodeUper(saved.roleCodeUper)
  }

  const handleConfirmDelete = async () => {
    if (!selectedRow) return

    setDeleteDialog((prev) => ({ ...prev, error: '' }))
    try {
      await deleteRole(selectedRow.roleCodeUper).unwrap()
      setSelectedRoleCodeUper(null)
      setDeleteDialog(emptyDeleteDialogState)
    } catch (errorValue) {
      setDeleteDialog((prev) => ({
        ...prev,
        error: extractApiErrorMessage(errorValue),
      }))
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setSearchAnchorEl(e.currentTarget)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => handleOpenRoleDialog('new')}>
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!hasSelection || !canEdit}
            onClick={() => handleOpenRoleDialog('edit')}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={actionToolbarBtnSx}
            disabled={!hasSelection || !canEdit}
            onClick={handleOpenDeleteDialog}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      <Popover
        open={searchAnchorEl !== null}
        anchorEl={searchAnchorEl}
        onClose={() => setSearchAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
      >
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Search roles"
            placeholder="Role code, name, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={() => {
              setSearchTerm('')
              setSearchAnchorEl(null)
            }}
          >
            Clear Search
          </Button>
        </Stack>
      </Popover>

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load roles.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
            count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={col.key} align="left" sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: SettingRole) =>
              TABLE_COLUMNS.map((col) => {
                const value = cellValue(row, col)
                return (
                  <TableCell key={col.key} align="left" sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" className="app-table-cell-text" title={value || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {value || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 3000, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowCode = Number.isFinite(idx) ? rows[idx]?.roleCodeUper : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowCode != null && rowCode === selectedRoleCodeUper}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowCode != null) setSelectedRoleCodeUper(rowCode)
                    }}
                  />
                )
              },
            }}
          />
        </Box>
      </Box>

      <SettingsRoleDialog
        open={roleDialogOpen}
        mode={roleDialogMode}
        initialRole={currentDialogRole}
        onClose={() => setRoleDialogOpen(false)}
        onSubmit={handleSaveRole}
        isSubmitting={isCreating || isUpdating}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(emptyDeleteDialogState)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: 'min(900px, 92vh)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: 'nowrap',
          }}
        >
          <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
            Delete Role
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
            <Button variant="outlined" size="small" onClick={() => setDeleteDialog(emptyDeleteDialogState)} sx={actionToolbarBtnSx} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" size="small" color="error" onClick={handleConfirmDelete} sx={actionToolbarBtnSx} disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent
          dividers
          sx={{
            pt: 0.75,
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            '& .MuiTypography-body2': { fontSize: '0.82rem' },
          }}
        >
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {deleteDialog.error ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {deleteDialog.error}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Do you want to delete Role code = '{selectedRow?.roleCodeUper ?? ''}' ?
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

